/** CEO 已批广告物料预算的只读明细。实际费用仍以付款确认后的运营费用台账为准。 */
import React, { useEffect, useRef, useState } from "react";
import { financeRead } from "./OperatingApprovals";
import { ResponsiveTable } from "../mobile/ResponsiveTable";
import { fmtD } from "../../lib/date";

const REFRESH_MS = 15000;
const PAGE_SIZE = 50;
const money = (value, currency) => `${currency === "CNY" ? "¥" : "$"}${Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function ApprovedAdMaterialPurchases({ api, zh, branch, branchName, range, category, group, refreshKey, invalidRange, onChanged }) {
  const [result, setResult] = useState(null);
  const [failure, setFailure] = useState(null);
  const [loadedPages, setLoadedPages] = useState({ key: "", count: 1 });
  const previous = useRef(null);
  const moreTarget = useRef(null);
  const queryKey = JSON.stringify({ branch, dateFrom: range.start || "", dateTo: range.end || "",
    category: category === "all" ? "" : category, group: group === "all" ? "" : group, refreshKey });
  const data = result?.key === queryKey ? result.data : null;
  const error = failure?.key === queryKey ? failure.message : null;
  const pageCount = loadedPages.key === queryKey ? loadedPages.count : 1;

  useEffect(() => {
    if (!data || pageCount >= data.pages || typeof IntersectionObserver !== "function" || !moreTarget.current) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setLoadedPages((old) => ({ key: queryKey, count: (old.key === queryKey ? old.count : 1) + 1 }));
    }, { rootMargin: "200px" });
    observer.observe(moreTarget.current);
    return () => observer.disconnect();
  }, [data, pageCount, queryKey]);

  useEffect(() => {
    if (invalidRange) return undefined;
    let active = true;
    let loading = false;
    let timer;
    let controller;
    const { refreshKey: ignored, ...params } = JSON.parse(queryKey);
    const sync = async () => {
      if (!active || document.hidden || loading) return;
      clearTimeout(timer);
      loading = true;
      controller = new AbortController();
      try {
        let page = 1;
        let pages = 1;
        let count = 0;
        const byRequest = new Map();
        do {
          const { data: response } = await financeRead(api, "/finance/operating-approved-purchases", {
            params: { ...params, page, pageSize: PAGE_SIZE }, signal: controller.signal, timeout: 20000,
          });
          count = response.count || 0;
          pages = response.pages || 1;
          (response.items || []).forEach((row) => byRequest.set(row.requestId, row));
          page += 1;
        } while (active && page <= Math.min(pageCount, pages));
        if (!active) return;
        const items = [...byRequest.values()].sort((a, b) => String(b.approvedAt || "").localeCompare(String(a.approvedAt || "")));
        const fingerprint = JSON.stringify(items.map((row) => [row.requestId, row.approvedAmount, row.payableAmount, row.paidAmount, row.remainingAmount]));
        setResult({ key: queryKey, data: { items, count, pages } });
        setFailure(null);
        if (previous.current?.key === queryKey && previous.current.pageCount === pageCount && previous.current.fingerprint !== fingerprint) onChanged();
        previous.current = { key: queryKey, pageCount, fingerprint };
      } catch (e) {
        if (active) setFailure({ key: queryKey, message: e?.response?.data?.detail || (zh ? "待入账采购申请同步失败" : "Unable to sync approved purchases") });
      } finally {
        loading = false;
        if (active && !document.hidden) timer = setTimeout(sync, REFRESH_MS);
      }
    };
    const onVisible = () => { if (!document.hidden) sync(); };
    sync();
    window.addEventListener("focus", onVisible);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      active = false;
      clearTimeout(timer);
      controller?.abort();
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [api, queryKey, pageCount, invalidRange, zh, onChanged]);

  return <section className="border-b border-slate-100" data-testid="op-approved-purchases" aria-busy={!data && !error}>
    <div className="px-4 py-3 flex flex-wrap items-center justify-between gap-2">
      <div>
        <h3 className="text-sm font-semibold text-slate-800">{zh ? "已批待入账 · 广告物料" : "Approved, not yet posted · Advertising materials"}</h3>
        <p className="mt-1 text-xs text-slate-500">{zh ? "按批准日期筛选；预算与待付金额不计入上方已入账费用合计。" : "Filtered by approval date; budgets and unpaid balances are excluded from posted expense totals."}</p>
      </div>
      {data && <span className="text-xs text-slate-500" data-testid="op-approved-purchases-count">{zh ? `共 ${data.count} 笔` : `${data.count} requests`}</span>}
    </div>
    {error && <p role="alert" className="px-4 pb-4 text-sm text-destructive">{error}{zh ? "，将自动重试。" : "; retrying automatically."}</p>}
    {!data && !error ? <p role="status" className="px-4 pb-4 text-sm text-slate-500">{zh ? "正在同步已批采购申请…" : "Syncing approved purchases…"}</p>
      : !data ? null : !data.items.length ? <p className="px-4 pb-4 text-sm text-slate-500">{zh ? "当前筛选下暂无已批待付款的广告物料申请。" : "No approved advertising material purchases await payment."}</p>
        : <>
            <ResponsiveTable mobile="scroll" className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500"><tr>
              {[zh ? "批准日期" : "Approved", zh ? "申请 / 采购单" : "Request / order", zh ? "付款主体" : "Entity", zh ? "批准预算" : "Approved budget", zh ? "已付" : "Paid", zh ? "待付" : "Unpaid"].map((title, i) =>
                <th key={title} className={`px-3 py-2.5 font-medium ${i >= 3 ? "text-right" : "text-left"}`}>{title}</th>)}
            </tr></thead>
            <tbody>{data.items.map((row) => <tr key={row.requestId} data-testid={`op-approved-purchase-${row.requestId}`} className="border-t border-slate-100 hover:bg-slate-50/60">
              <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{fmtD(row.approvalDate || row.approvedAt)}</td>
              <td className="px-3 py-2.5 min-w-[190px] max-w-[260px]">
                <span className="block font-medium text-slate-800 truncate" title={row.requestNo || row.requestId}>{row.requestNo || row.requestId}</span>
                <span className="block text-xs text-slate-500 truncate" title={[row.requester, row.supplierName, row.note].filter(Boolean).join(" · ")}>{[row.requester, row.supplierName].filter(Boolean).join(" · ") || "—"}</span>
                {row.note && <span className="block text-xs text-slate-500 truncate" title={row.note}>{row.note}</span>}
                {row.orderNo && <span className="block text-xs text-slate-500 truncate" title={row.orderNo}>{row.orderNo}</span>}
                {!row.orderId && <span className="block text-xs text-amber-700">{zh ? "采购单同步中" : "Order syncing"}</span>}
              </td>
              <td className="px-3 py-2.5 whitespace-nowrap">{branchName(row.branch)}</td>
              <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">
                {money(row.approvedAmount, row.currency)}
                {Math.abs(Number(row.payableAmount || 0) - Number(row.approvedAmount || 0)) > 0.005 &&
                  <span className="block text-xs text-slate-500">{zh ? "实际应付" : "Payable"} {money(row.payableAmount, row.currency)}</span>}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums whitespace-nowrap">{money(row.paidAmount, row.currency)}</td>
              <td className="px-3 py-2.5 text-right font-semibold tabular-nums whitespace-nowrap" data-testid={`op-approved-purchase-remaining-${row.requestId}`}>
                {money(row.remainingAmount, row.currency)}
                <span className="block text-xs font-normal text-slate-500">{row.paymentStatus === "partial" ? (zh ? "部分付款" : "Partially paid") : (zh ? "待付款" : "Awaiting payment")}</span>
              </td>
            </tr>)}</tbody>
            </ResponsiveTable>
            {pageCount < data.pages && <p ref={moreTarget} className="px-4 py-3 text-center text-xs text-slate-500" role="status">
              {zh ? `向下滚动加载更多 · 已显示 ${data.items.length} / ${data.count} 笔` : `Scroll for more · ${data.items.length} / ${data.count} shown`}
            </p>}
          </>}
  </section>;
}
